import { useCallback, useEffect, useRef, useState } from 'react';

export type LedgerReason = 'usage' | 'refund' | 'admin_grant' | 'purchase' | 'adjustment';

export type LedgerItem = {
  id: number;
  delta: number;
  balance_after: number;
  reason: LedgerReason;
  endpoint: string | null;
  activity_log_id: number | null;
  payment_id: number | null;
  note: string | null;
  created_at: string;
};

export function useCreditsBalance(opts: { pollMs?: number } = {}) {
  const [credits, setCredits] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/credits/balance', { credentials: 'include' });
      if (!res.ok) throw new Error(`balance fetch failed: ${res.status}`);
      const data = (await res.json()) as { credits: number };
      setCredits(data.credits);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void fetchOnce();
    const ms = opts.pollMs ?? 0;
    if (ms > 0) {
      const id = setInterval(() => void fetchOnce(), ms);
      return () => clearInterval(id);
    }
  }, [fetchOnce, opts.pollMs]);

  return { credits, error, refresh: fetchOnce };
}

export const LEDGER_PAGE_SIZE = 50;

// [from, to) bounds on created_at as ISO instants; either side may be open.
export type LedgerRange = { from?: string; to?: string };

export function useCreditLedger(
  opts: { pollMs?: number; pageSize?: number; range?: LedgerRange } = {}
) {
  const pageSize = opts.pageSize ?? LEDGER_PAGE_SIZE;
  const from = opts.range?.from;
  const to = opts.range?.to;
  const [items, setItems] = useState<LedgerItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // zero-based
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryRef = useRef({ page, pageSize, from, to });
  queryRef.current = { page, pageSize, from, to };

  // A new window or page size makes the old page number meaningless.
  useEffect(() => {
    setPage(0);
  }, [from, to, pageSize]);

  const fetchOnce = useCallback(async () => {
    setLoading(true);
    try {
      const q = queryRef.current;
      const params = new URLSearchParams({
        limit: String(q.pageSize),
        offset: String(q.page * q.pageSize),
      });
      if (q.from) params.set('from', q.from);
      if (q.to) params.set('to', q.to);
      const res = await fetch(`/api/v1/credits/ledger?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`ledger fetch failed: ${res.status}`);
      const data = (await res.json()) as { items: LedgerItem[]; total?: number };
      // Drop a response that a newer page/filter has already superseded.
      const now = queryRef.current;
      if (now.page !== q.page || now.pageSize !== q.pageSize || now.from !== q.from || now.to !== q.to) return;
      setItems(data.items);
      setTotal(Number(data.total ?? data.items.length));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOnce();
    const ms = opts.pollMs ?? 0;
    if (ms > 0) {
      const id = setInterval(() => void fetchOnce(), ms);
      return () => clearInterval(id);
    }
  }, [fetchOnce, opts.pollMs, page, pageSize, from, to]);

  return { items, total, page, pageSize, setPage, loading, error, refresh: fetchOnce };
}

export function formatCredits(n: number): string {
  return n.toLocaleString();
}

export function creditsToUsd(credits: number): string {
  return (credits / 100).toFixed(2);
}
